/**
 * Lumina Quiz — AI Skin Quiz scoring engine
 * Extracted from sections/ai-skin-quiz.liquid (A1-17 fix).
 *
 * The section emits:
 *   <section data-sq-quiz data-section-id="{{ section.id }}" id="ai-skin-quiz-{{ section.id }}">
 *   <script type="application/json" id="sq-config-{{ section.id }}" data-sq-config>{...}</script>
 *
 * This file boots one instance per matched [data-sq-quiz] section.
 */
(function () {
  'use strict';

  function initQuiz(sectionEl) {
    if (!sectionEl) return;
    // Guard: the site-wide launcher in layout/theme.liquid injects a quiz that
    // was fetched from the homepage and calls bootAll() again, so the same
    // element can be handed to us twice. Binding twice would double every
    // click handler.
    if (sectionEl.dataset.sqBooted === 'true') return;
    sectionEl.dataset.sqBooted = 'true';

  /* ══════════════════════════════════════════════════════════════
     BOOTSTRAP — config load (sectionEl is the section root element)
     ══════════════════════════════════════════════════════════════ */

  var WEIGHTS      = { w_trending: 35, w_campaign: 20, w_popular: 15, exposure_penalty: 50 };
  var MONEY_FORMAT = '{{amount}}';
  var SQ_I18N = {
    progress_label_template:    'Question __C__ of __T__',
    step_fallback_template:     'Step __NUM__',
    more_options:               'More options',
    out_of_stock:               'Out of stock',
    assign_products:            'Assign products in the editor',
    swap_to_template:           'Swap to __TITLE__',
    products_count_template:    '__COUNT__ products · Complete routine',
    add_routine_aria:           'Add full routine to cart',
    add_routine_label_template: 'Add Full Routine to Cart — __PRICE__',
    adding:                     'Adding to Cart…',
    added:                      '✓ Added to Cart — View Cart →',
    error:                      'Something went wrong — try again',
    badges: {
      trending:   'Trending',
      popular:    'Popular now',
      beginner:   'Beginner-safe',
      hidden_gem: 'Hidden gem',
      campaign:   'Campaign'
    }
  };

  (function () {
    var cfg = sectionEl.querySelector('[data-sq-config]') || document.getElementById('sq-config-' + sectionEl.dataset.sectionId);
    if (!cfg) return;
    try {
      var parsed = JSON.parse(cfg.textContent);
      if (parsed.weights)      WEIGHTS      = parsed.weights;
      if (parsed.money_format) MONEY_FORMAT = parsed.money_format;
      if (parsed.i18n) {
        for (var k in parsed.i18n) {
          if (Object.prototype.hasOwnProperty.call(parsed.i18n, k)) SQ_I18N[k] = parsed.i18n[k];
        }
      }
    } catch (e) { /* keep defaults */ }
  })();

  /* Token replacement for translated templates */
  function sqFmt(template, replacements) {
    var out = String(template == null ? '' : template);
    for (var k in replacements) {
      if (Object.prototype.hasOwnProperty.call(replacements, k)) {
        out = out.split(k).join(String(replacements[k]));
      }
    }
    return out;
  }

  /* ══════════════════════════════════════════════════════════════
     TRACKER — exposure balancing & event analytics via localStorage
     ══════════════════════════════════════════════════════════════ */
  var SQTracker = {
    KEY: 'lumina_sq_v2',

    _data: null,
    _read: function () {
      if (this._data) return this._data;
      try { this._data = JSON.parse(localStorage.getItem(this.KEY) || '{}'); }
      catch (e) { this._data = {}; }
      return this._data;
    },
    _write: function () {
      try { localStorage.setItem(this.KEY, JSON.stringify(this._data)); } catch (e) {}
    },

    track: function (id, event) {
      var d = this._read();
      var k = String(id) + ':' + event;
      d[k] = (d[k] || 0) + 1;
      this._write();
      /* Dispatch custom event so analytics integrations can listen */
      try {
        document.dispatchEvent(new CustomEvent('sq:track', {
          bubbles: true,
          detail: { productId: id, event: event }
        }));
      } catch (e) {}
    },

    getCount: function (id, event) {
      return this._read()[String(id) + ':' + event] || 0;
    },

    /* Returns a 0.3–1.0 multiplier that penalises overexposed products */
    exposureFactor: function (id, penaltyStrength) {
      var count = this.getCount(id, 'impression');
      if (count <= 3) return 1.0;
      /* Penalty rate scales with the slider (0–100 → 0–0.12 per extra impression) */
      var rate = ((penaltyStrength || 50) / 100) * 0.12;
      return Math.max(0.3, 1.0 - (count - 3) * rate);
    }
  };

  /* ══════════════════════════════════════════════════════════════
     SCORING ENGINE
     ══════════════════════════════════════════════════════════════
     Signals used (all derived from product tags — no AJAX needed):
       quiz-trending    → w_trending bonus
       quiz-campaign    → w_campaign bonus
       quiz-popular     → w_popular bonus
       quiz-hidden-gem  → small organic boost (helps underexposed products)
       quiz-boost-high  → ×1.6 multiplier
       quiz-boost-med   → ×1.3 multiplier
       OOS product      → hard-filtered out (score = -Infinity)
       Exposure count   → decay penalty via SQTracker.exposureFactor()
     ══════════════════════════════════════════════════════════════ */
  var SQScore = {
    score: function (candidate, weights) {
      /* Hard filter: out-of-stock products never win */
      if (!candidate.available) return -Infinity;

      var tags = (candidate.tags || '').toLowerCase();
      var s    = 50; /* baseline score */

      /* Tag-driven bonuses */
      if (tags.indexOf('quiz-trending')  !== -1) s += (weights.w_trending  || 35);
      if (tags.indexOf('quiz-campaign')  !== -1) s += (weights.w_campaign  || 20);
      if (tags.indexOf('quiz-popular')   !== -1) s += (weights.w_popular   || 15);
      if (tags.indexOf('quiz-hidden-gem')!== -1) s += 8;  /* organic visibility nudge */

      /* Manual override multipliers */
      if (tags.indexOf('quiz-boost-high') !== -1) s *= 1.6;
      if (tags.indexOf('quiz-boost-med')  !== -1) s *= 1.3;

      /* Exposure balancing — penalise products this user has seen a lot */
      s *= SQTracker.exposureFactor(candidate.id, weights.exposure_penalty);

      return s;
    },

    /* Score + rank candidate array; returns sorted copy (best first) */
    rank: function (candidates, weights) {
      var scored = [];
      for (var i = 0; i < candidates.length; i++) {
        scored.push({ c: candidates[i], s: SQScore.score(candidates[i], weights) });
      }
      scored.sort(function (a, b) { return b.s - a.s; });
      var out = [];
      for (var j = 0; j < scored.length; j++) out.push(scored[j].c);
      return out;
    },

    /* Select winner + alternates for one step */
    selectStep: function (candidates, weights) {
      /* Prefer in-stock; fall back to full pool if everything is OOS */
      var inStock = [];
      for (var i = 0; i < candidates.length; i++) {
        if (candidates[i].available) inStock.push(candidates[i]);
      }
      var pool   = inStock.length > 0 ? inStock : candidates;
      var ranked = SQScore.rank(pool, weights);
      return { winner: ranked[0] || null, alternates: ranked.slice(1) };
    }
  };

  /* ══════════════════════════════════════════════════════════════
     DATA READER — extracts candidate objects from hidden DOM nodes
     ══════════════════════════════════════════════════════════════ */
  function readCandidates(stepEl) {
    var nodes = stepEl.querySelectorAll('.sq-cand');
    var out   = [];
    for (var i = 0; i < nodes.length; i++) {
      var n  = nodes[i];
      var id = n.getAttribute('data-id');
      if (!id) continue;
      out.push({
        id:         id,
        variant_id: n.getAttribute('data-vid')        || '',
        title:      n.getAttribute('data-title')      || '',
        price:      parseInt(n.getAttribute('data-price') || '0', 10),
        price_fmt:  n.getAttribute('data-price-fmt')  || '',
        url:        n.getAttribute('data-url')        || '',
        image:      n.getAttribute('data-image')      || '',
        available:  n.getAttribute('data-available')  !== 'false',
        tags:       n.getAttribute('data-tags')       || '',
        tip:        n.getAttribute('data-tip')        || ''
      });
    }
    return out;
  }

  /* ══════════════════════════════════════════════════════════════
     UTILITIES
     ══════════════════════════════════════════════════════════════ */

  /* HTML-escape a value for safe innerHTML insertion */
  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

 /* Professional Shopify-safe money formatter */
/* Fixes duplicated values like:
419419,00419419.00{{amount}}
caused by partial / incorrect token replacement */

function formatMoney(cents) {
  if (cents == null || isNaN(cents)) return '';

  cents = parseInt(cents, 10);

  var amount = (cents / 100).toFixed(2);
  var amountNoDecimals = Math.round(cents / 100).toString();
  var amountWithComma = amount.replace('.', ',');
  var amountNoDecimalsComma = amountNoDecimals;

  var format = (typeof MONEY_FORMAT !== 'undefined' && MONEY_FORMAT)
    ? MONEY_FORMAT
    : '{{ amount }}';

  /*
    Shopify money formats may contain:
    {{ amount }}
    {{ amount_no_decimals }}
    {{ amount_with_comma_separator }}
    {{ amount_no_decimals_with_comma_separator }}

    IMPORTANT:
    Replace using regex + global cleanup
    because .replace() once can leave partial tokens behind
  */

  format = format
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimalsComma)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
    .replace(/\{\{\s*amount\s*\}\}/g, amount);

  /* Final safety cleanup for broken leftover liquid tags */
  format = format.replace(/\{\{.*?\}\}/g, '');

  return format.trim();
}

  /* Badge HTML from product tags */
  var BADGE_MAP = {
    'quiz-trending':    { i18n: 'trending',   cls: 'sq-badge--trending'  },
    'quiz-popular':     { i18n: 'popular',    cls: 'sq-badge--popular'   },
    'quiz-beginner':    { i18n: 'beginner',   cls: 'sq-badge--beginner'  },
    'quiz-hidden-gem':  { i18n: 'hidden_gem', cls: 'sq-badge--hidden'    },
    'quiz-campaign':    { i18n: 'campaign',   cls: 'sq-badge--campaign'  }
  };

  function badgeHTML(tags) {
    var t = (tags || '').toLowerCase();
    var labels = (SQ_I18N && SQ_I18N.badges) || {};
    for (var key in BADGE_MAP) {
      if (Object.prototype.hasOwnProperty.call(BADGE_MAP, key) && t.indexOf(key) !== -1) {
        var b = BADGE_MAP[key];
        var text = labels[b.i18n] || '';
        return '<div class="sq-badge-wrap"><span class="sq-badge ' + b.cls + '">' + esc(text) + '</span></div>';
      }
    }
    return '';
  }

  /* ══════════════════════════════════════════════════════════════
     RENDERER — builds step card + swap panel HTML strings
     ══════════════════════════════════════════════════════════════ */

  function stepCardHTML(stepNum, stepLabel, winner, alternates) {
    if (!winner) {
      /* Placeholder — no products assigned */
      return (
        '<div class="sq-step-card__badge">' +
          '<span class="sq-step-card__num">' + stepNum + '</span>' +
          '<span>' + esc(stepLabel) + '</span>' +
        '</div>' +
        '<div class="sq-step-card__placeholder">' +
          '<p>' + esc(stepLabel) + '</p>' +
          '<p class="sq-placeholder-note">' + esc(SQ_I18N.assign_products) + '</p>' +
        '</div>'
      );
    }

    var oosTag = !winner.available ? '<span class="sq-oos-tag">' + esc(SQ_I18N.out_of_stock) + '</span>' : '';

    var imgHTML = winner.image
      ? '<a href="' + esc(winner.url) + '" class="sq-step-card__img-wrap" tabindex="-1" aria-hidden="true">' +
          '<img src="' + esc(winner.image) + '" alt="' + esc(winner.title) + '" class="sq-step-card__img" loading="lazy">' +
        '</a>'
      : '<div class="sq-step-card__img-wrap" style="aspect-ratio:3/4;background:var(--color-primary-light)"></div>';

    var tipHTML = winner.tip
      ? '<p class="sq-step-card__tip">💡 ' + esc(winner.tip) + '</p>'
      : '';

    var swapHTML = alternates && alternates.length > 0
      ? swapPanelHTML(stepNum, alternates)
      : '';

    return (
      '<div class="sq-step-card__badge">' +
        '<span class="sq-step-card__num">' + stepNum + '</span>' +
        '<span>' + esc(stepLabel) + '</span>' +
      '</div>' +
      badgeHTML(winner.tags) +
      imgHTML +
      '<div class="sq-step-card__info">' +
        '<a href="' + esc(winner.url) + '" class="sq-step-card__title">' + esc(winner.title) + '</a>' +
        '<p class="sq-step-card__price">' + esc(winner.price_fmt) + oosTag + '</p>' +
        tipHTML +
      '</div>' +
      swapHTML
    );
  }

  function swapPanelHTML(stepNum, alternates) {
    var items = '';
    for (var i = 0; i < alternates.length; i++) {
      var p     = alternates[i];
      var thumb = p.image
        ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" class="sq-swap-thumb" loading="lazy">'
        : '<div class="sq-swap-thumb sq-swap-thumb--empty"></div>';
      var oosNote = !p.available ? '<span class="sq-swap-oos">' + esc(SQ_I18N.out_of_stock) + '</span>' : '';
      var swapAria = sqFmt(SQ_I18N.swap_to_template, { '__TITLE__': p.title || '' });
      items += (
        '<button class="sq-swap-option"' +
          ' data-swap-step="' + stepNum + '"' +
          ' data-id="'        + esc(p.id)         + '"' +
          ' data-vid="'       + esc(p.variant_id)  + '"' +
          ' data-title="'     + esc(p.title)       + '"' +
          ' data-price="'     + p.price            + '"' +
          ' data-price-fmt="' + esc(p.price_fmt)   + '"' +
          ' data-url="'       + esc(p.url)         + '"' +
          ' data-image="'     + esc(p.image)       + '"' +
          ' data-available="' + p.available        + '"' +
          ' data-tags="'      + esc(p.tags)        + '"' +
          ' data-tip="'       + esc(p.tip)         + '"' +
          ' aria-label="' + esc(swapAria) + '">' +
          thumb +
          '<span class="sq-swap-info">' +
            '<span class="sq-swap-name">' + esc(p.title) + '</span>' +
            '<span class="sq-swap-price">' + esc(p.price_fmt) + oosNote + '</span>' +
          '</span>' +
        '</button>'
      );
    }
    return (
      '<div class="sq-swap-panel" data-swap-panel="' + stepNum + '">' +
        '<button class="sq-swap-toggle" data-swap-step-toggle="' + stepNum + '" aria-expanded="false">' +
          esc(SQ_I18N.more_options) + ' <span class="sq-swap-arrow">▾</span>' +
        '</button>' +
        '<div class="sq-swap-options" hidden>' + items + '</div>' +
      '</div>'
    );
  }

  /* ── Main block renderer — scores all steps and injects HTML ── */
  function renderResultBlock(blockEl) {
    var stepEls = blockEl.querySelectorAll('.sq-step-candidates');
    if (!stepEls.length) return;

    var variantIds   = [];
    var routineTotal = 0;
    var gridHTML     = '<div class="sq-routine-grid">';

    for (var i = 0; i < stepEls.length; i++) {
      var stepEl    = stepEls[i];
      var stepNum   = parseInt(stepEl.getAttribute('data-step'), 10);
      var stepLabel = stepEl.getAttribute('data-step-label') || sqFmt(SQ_I18N.step_fallback_template, { '__NUM__': stepNum });
      var candidates = readCandidates(stepEl);
      var sel        = SQScore.selectStep(candidates, WEIGHTS);
      var winner     = sel.winner;
      var alts       = sel.alternates;

      /* Track impressions for all candidates presented to this user */
      for (var ci = 0; ci < candidates.length; ci++) {
        SQTracker.track(candidates[ci].id, 'impression');
      }

      var cardInner = stepCardHTML(stepNum, stepLabel, winner, alts);
      var priceAttr = winner ? winner.price : 0;
      var vidAttr   = winner ? esc(winner.variant_id) : '';

      gridHTML += '<div class="sq-step-card" data-step-card="' + stepNum + '" data-variant="' + vidAttr + '" data-price="' + priceAttr + '">' +
        cardInner +
      '</div>';

      if (winner) {
        variantIds.push(winner.variant_id);
        routineTotal += winner.price || 0;
      }
    }

    gridHTML += '</div>'; /* /sq-routine-grid */

    /* ── Checkout bar ── */
    var checkoutHTML = '';
    if (variantIds.length > 0) {
      var totalFmt   = formatMoney(routineTotal);
      var countLabel = sqFmt(SQ_I18N.products_count_template, { '__COUNT__': variantIds.length });
      var atcLabel   = sqFmt(SQ_I18N.add_routine_label_template, { '__PRICE__': totalFmt });
      checkoutHTML = (
        '<div class="sq-checkout-bar">' +
          '<div class="sq-checkout-bar__meta">' +
            '<span class="sq-checkout-bar__count">' + esc(countLabel) + '</span>' +
            '<span class="sq-checkout-bar__total" data-sq-total>' + totalFmt + '</span>' +
          '</div>' +
          '<button class="btn btn-primary sq-atc-all" data-sq-atc aria-label="' + esc(SQ_I18N.add_routine_aria) + '">' +
            '<svg class="sq-cart-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
              '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>' +
              '<line x1="3" y1="6" x2="21" y2="6"/>' +
              '<path d="M16 10a4 4 0 0 1-8 0"/>' +
            '</svg>' +
            '<span class="sq-atc-label">' + esc(atcLabel) + '</span>' +
          '</button>' +
        '</div>'
      );
    }

    /* Inject rendered HTML */
    var target = blockEl.querySelector('.sq-render-target');
    if (target) target.innerHTML = gridHTML + checkoutHTML;

    /* Wire interaction handlers */
    wireSwapToggles(blockEl);
    wireAtcButton(blockEl);
  }

  /* ══════════════════════════════════════════════════════════════
     SWAP UI — toggle panel + product replacement
     ══════════════════════════════════════════════════════════════ */
  function wireSwapToggles(blockEl) {
    /* Toggle open/close */
    var toggles = blockEl.querySelectorAll('[data-swap-step-toggle]');
    for (var t = 0; t < toggles.length; t++) {
      (function (btn) {
        if (btn._sqWired) return;
        btn._sqWired = true;
        var stepNum = btn.getAttribute('data-swap-step-toggle');
        var panel   = blockEl.querySelector('[data-swap-panel="' + stepNum + '"] .sq-swap-options');

        btn.addEventListener('click', function () {
          var isOpen = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!isOpen));
          if (panel) panel.hidden = isOpen;
          var arrow = btn.querySelector('.sq-swap-arrow');
          if (arrow) arrow.textContent = isOpen ? '▾' : '▴';
        });
      })(toggles[t]);
    }

    /* Swap option click — replace displayed product */
    var opts = blockEl.querySelectorAll('.sq-swap-option');
    for (var s = 0; s < opts.length; s++) {
      (function (opt) {
        if (opt._sqWired) return;
        opt._sqWired = true;

        opt.addEventListener('click', function () {
          var stepNum = opt.getAttribute('data-swap-step');
          var card    = blockEl.querySelector('[data-step-card="' + stepNum + '"]');
          if (!card) return;

          /* ── Update product display ── */
          var imgWrap = card.querySelector('.sq-step-card__img-wrap');
          var imgEl   = imgWrap ? imgWrap.querySelector('img') : null;
          var titleEl = card.querySelector('.sq-step-card__title');
          var priceEl = card.querySelector('.sq-step-card__price');
          var tipEl   = card.querySelector('.sq-step-card__tip');
          var bdgWrap = card.querySelector('.sq-badge-wrap');

          var newImg   = opt.getAttribute('data-image')    || '';
          var newUrl   = opt.getAttribute('data-url')      || '';
          var newTitle = opt.getAttribute('data-title')    || '';
          var newPriceFmt = opt.getAttribute('data-price-fmt') || '';
          var newTip   = opt.getAttribute('data-tip')      || '';
          var newTags  = opt.getAttribute('data-tags')     || '';
          var newVid   = opt.getAttribute('data-vid')      || '';
          var newPrice = parseInt(opt.getAttribute('data-price') || '0', 10);
          var newId    = opt.getAttribute('data-id')       || '';
          var newAvail = opt.getAttribute('data-available') !== 'false';

          if (imgWrap) { imgWrap.href = newUrl; }
          if (imgEl)   { imgEl.src = newImg; imgEl.alt = newTitle; }
          if (titleEl) { titleEl.href = newUrl; titleEl.textContent = newTitle; }
          if (priceEl) { priceEl.textContent = newPriceFmt; }
          if (tipEl && newTip) tipEl.textContent = '💡 ' + newTip;
          if (bdgWrap) bdgWrap.outerHTML = badgeHTML(newTags);

          /* ── Update card's variant + price data attrs ── */
          card.setAttribute('data-variant', newVid);
          card.setAttribute('data-price', String(newPrice));

          /* ── Rebuild ATC total ── */
          updateAtcTotal(blockEl);

          /* ── Track swap event ── */
          SQTracker.track(newId, 'swap');

          /* ── Close swap panel ── */
          var toggle = card.querySelector('[data-swap-step-toggle="' + stepNum + '"]');
          var panel  = card.querySelector('.sq-swap-options');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          if (panel)  panel.hidden = true;
          var arrow = toggle ? toggle.querySelector('.sq-swap-arrow') : null;
          if (arrow) arrow.textContent = '▾';
        });
      })(opts[s]);
    }
  }

  /* ── Recalculate + display updated total after a swap ── */
  function updateAtcTotal(blockEl) {
    var cards  = blockEl.querySelectorAll('[data-step-card]');
    var total  = 0;
    var count  = 0;
    for (var i = 0; i < cards.length; i++) {
      var vid = cards[i].getAttribute('data-variant');
      var p   = parseInt(cards[i].getAttribute('data-price') || '0', 10);
      if (vid) { total += p; count++; }
    }
    var totalEl = blockEl.querySelector('[data-sq-total]');
    var labelEl = blockEl.querySelector('.sq-atc-label');
    var countEl = blockEl.querySelector('.sq-checkout-bar__count');
    var fmt = formatMoney(total);
    if (totalEl) totalEl.textContent = fmt;
    if (labelEl) labelEl.textContent = sqFmt(SQ_I18N.add_routine_label_template, { '__PRICE__': fmt });
    if (countEl) countEl.textContent = sqFmt(SQ_I18N.products_count_template, { '__COUNT__': count });
  }

  /* ══════════════════════════════════════════════════════════════
     ADD TO CART
     ══════════════════════════════════════════════════════════════ */
  function wireAtcButton(blockEl) {
    var btn = blockEl.querySelector('[data-sq-atc]');
    if (!btn || btn._sqWired) return;
    btn._sqWired = true;

    var labelEl      = btn.querySelector('.sq-atc-label');
    var originalText = labelEl ? labelEl.textContent : '';

    btn.addEventListener('click', function () {
      if (btn.disabled) return;

      /* Collect current variant IDs from step cards */
      var variantIds = [];
      var cards = blockEl.querySelectorAll('[data-step-card]');
      for (var i = 0; i < cards.length; i++) {
        var vid = cards[i].getAttribute('data-variant');
        if (vid) variantIds.push(vid);
      }
      if (variantIds.length === 0) return;

      btn.disabled = true;
      if (labelEl) labelEl.textContent = SQ_I18N.adding;

      var items = [];
      for (var j = 0; j < variantIds.length; j++) {
        items.push({ id: parseInt(variantIds[j], 10), quantity: 1 });
        SQTracker.track(variantIds[j], 'add_to_cart');
      }

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ items: items })
      })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart error ' + res.status);
        return res.json();
      })
      .then(function () {
        if (labelEl) labelEl.textContent = SQ_I18N.added;
        btn.classList.add('is-success');

        // Close the quiz dialog, if the quiz is being shown in one. Dispatched
        // BEFORE the cart drawer opens so the two transitions do not overlap.
        // The launcher scripts in sections/ai-skin-quiz.liquid and
        // layout/theme.liquid listen for this; when the quiz is inline there
        // is no listener and nothing happens.
        document.dispatchEvent(new CustomEvent('lumina:quiz:added', {
          detail: { sectionId: sectionEl.dataset.sectionId }
        }));

        if (window.Lumina && window.Lumina.cart && typeof window.Lumina.cart.open === 'function') {
          window.Lumina.cart.open();
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }

        setTimeout(function () {
          if (labelEl) labelEl.textContent = originalText;
          btn.classList.remove('is-success');
          btn.disabled = false;
        }, 4000);
      })
      .catch(function () {
        if (labelEl) labelEl.textContent = SQ_I18N.error;
        btn.disabled = false;
        setTimeout(function () {
          if (labelEl) labelEl.textContent = originalText;
        }, 3000);
      });
    });
  }

  /* ══════════════════════════════════════════════════════════════
     QUIZ CONTROLLER (original flow — unchanged from v1)
     ══════════════════════════════════════════════════════════════ */
  var questionEls = sectionEl.querySelectorAll('.sq-question[data-sq-q]');
  var totalQ      = questionEls.length || 1;
  var BASE_SCORES = { oily: 0, dry: 0, combination: 0, sensitive: 0, normal: 0 };
  var scores      = Object.assign({}, BASE_SCORES);
  var currentQ    = 1;

  function show(screen) {
    var all = sectionEl.querySelectorAll('.sq-screen');
    for (var i = 0; i < all.length; i++) all[i].classList.remove('is-active');
    var el = sectionEl.querySelector('[data-sq-screen="' + screen + '"]');
    if (el) el.classList.add('is-active');
  }

  function updateProgress(q) {
    var fill  = sectionEl.querySelector('[data-sq-progress-fill]');
    var label = sectionEl.querySelector('[data-sq-progress-label]');
    if (fill)  fill.style.width  = Math.round(q / totalQ * 100) + '%';
    if (label) label.textContent = sqFmt(SQ_I18N.progress_label_template, { '__C__': q, '__T__': totalQ });
  }

  function showQuestion(n) {
    var all = sectionEl.querySelectorAll('.sq-question');
    for (var i = 0; i < all.length; i++) all[i].classList.remove('is-active');
    var qEl = sectionEl.querySelector('[data-sq-q="' + n + '"]');
    if (qEl) qEl.classList.add('is-active');
    updateProgress(n);
  }

  /* Start */
  var startBtns = sectionEl.querySelectorAll('[data-sq-action="start"]');
  for (var sb = 0; sb < startBtns.length; sb++) {
    startBtns[sb].addEventListener('click', function () {
      scores   = Object.assign({}, BASE_SCORES);
      currentQ = 1;
      var opts = sectionEl.querySelectorAll('.sq-option');
      for (var i = 0; i < opts.length; i++) opts[i].classList.remove('is-selected');
      showQuestion(1);
      show('quiz');
    });
  }

  /* Answer click */
  var answerBtns = sectionEl.querySelectorAll('.sq-option');
  for (var ab = 0; ab < answerBtns.length; ab++) {
    answerBtns[ab].addEventListener('click', (function (opt) {
      return function () {
        var group = opt.closest('.sq-options');
        if (group) {
          var siblings = group.querySelectorAll('.sq-option');
          for (var i = 0; i < siblings.length; i++) siblings[i].classList.remove('is-selected');
        }
        opt.classList.add('is-selected');

        var answer = opt.getAttribute('data-sq-answer') || '';
        var parts  = answer.split(',');
        for (var p = 0; p < parts.length; p++) {
          var kv = parts[p].trim().split(':');
          if (kv.length === 2 && Object.prototype.hasOwnProperty.call(scores, kv[0])) {
            scores[kv[0]] += parseInt(kv[1], 10) || 0;
          }
        }

        setTimeout(function () {
          currentQ++;
          if (currentQ <= totalQ) { showQuestion(currentQ); }
          else { showResult(); }
        }, 280);
      };
    })(answerBtns[ab]));
  }

  /* ── Show result (UPGRADED — now calls scoring engine) ── */
  function showResult() {
    /* Determine winning skin type */
    var winner  = 'normal';
    var highest = -1;
    var keys    = Object.keys(scores);
    for (var i = 0; i < keys.length; i++) {
      if (scores[keys[i]] > highest) { highest = scores[keys[i]]; winner = keys[i]; }
    }

    /* Find matching result block; fall back to first */
    var blocks     = sectionEl.querySelectorAll('[data-skin-result]');
    var matchBlock = null;
    for (var b = 0; b < blocks.length; b++) {
      if (blocks[b].getAttribute('data-skin-result') === winner) { matchBlock = blocks[b]; break; }
    }
    if (!matchBlock && blocks.length > 0) matchBlock = blocks[0];

    if (matchBlock) {
      /* Update result header copy */
      var labelEl   = sectionEl.querySelector('[data-sq-result-type-label]');
      var taglineEl = sectionEl.querySelector('[data-sq-result-tagline]');
      var descEl    = sectionEl.querySelector('[data-sq-result-desc]');
      if (labelEl)   labelEl.textContent   = matchBlock.getAttribute('data-label')   || '';
      if (taglineEl) taglineEl.textContent = matchBlock.getAttribute('data-tagline') || '';
      if (descEl)    descEl.textContent    = matchBlock.getAttribute('data-desc')    || '';

      /* Hide all blocks, show matched */
      for (var bl = 0; bl < blocks.length; bl++) blocks[bl].style.display = 'none';
      matchBlock.style.display = 'block';

      /* ✦ SCORING ENGINE: render on first show; re-render on retake ── */
      if (!matchBlock._sqRendered) {
        renderResultBlock(matchBlock);
        matchBlock._sqRendered = true;
      }

      SQTracker.track(winner, 'result_shown');
    }

    show('result');
    sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ── Retake ── */
  var retakeBtns = sectionEl.querySelectorAll('[data-sq-action="retake"]');
  for (var rb = 0; rb < retakeBtns.length; rb++) {
    retakeBtns[rb].addEventListener('click', function () {
      scores   = Object.assign({}, BASE_SCORES);
      currentQ = 1;

      var opts = sectionEl.querySelectorAll('.sq-option');
      for (var i = 0; i < opts.length; i++) opts[i].classList.remove('is-selected');

      /* Clear rendered state so scoring re-runs with fresh exposure data */
      var blocks = sectionEl.querySelectorAll('[data-skin-result]');
      for (var b = 0; b < blocks.length; b++) {
        blocks[b]._sqRendered = false;
        var tgt = blocks[b].querySelector('.sq-render-target');
        if (tgt) tgt.innerHTML = '';
      }

      showQuestion(1);
      show('quiz');
    });
  }


  }

  function bootAll() {
    var sections = document.querySelectorAll('[data-sq-quiz]');
    for (var i = 0; i < sections.length; i++) initQuiz(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Theme editor re-renders a section's HTML in place; boot whatever it gave us.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-sq-quiz]') : null;
    if (el) initQuiz(el);
  });

  // Public hook so markup injected after load (the site-wide quiz dialog) can
  // be booted without re-running the whole file.
  window.LuminaQuiz = window.LuminaQuiz || {};
  window.LuminaQuiz.init    = initQuiz;
  window.LuminaQuiz.bootAll = bootAll;
})();
